import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
    Box,
    Typography,
    Paper,
    Popper,
    ClickAwayListener,
    Button,
    Skeleton,
    IconButton,
    useMediaQuery,
} from "@mui/material";
import {
    NotificationsNoneOutlined,
    CheckOutlined,
    Close,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import utils from "../../universal/utils";
import NotificationItem from "./NotificationItem.jsx";
import { colors } from "../../theme/colors";
import { AnimatePresence, motion } from "framer-motion";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import BackdropOverlay from "../common/BackdropOverlay.jsx";

const MotionPaper = motion(Paper);

const basePath = utils.getEdgeBasePath();
const MAX_SKELETONS = 4;
const REFETCH_COOLDOWN_MS = 4000;

const MARK_ALL_READ_SX = {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: "0.28rem",
    border: "none",
    background: "none",
    p: 0,
    m: 0,
    cursor: "pointer",
    font: "inherit",
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    color: colors.text.secondary,
    transition: "color 180ms ease",
    whiteSpace: "nowrap",
    "&:hover": {
        color: colors.primary.main,
    },
};

const CLOSE_BUTTON_SX = {
    width: "1.9rem",
    height: "1.9rem",
    borderRadius: "0.55rem",
    color: colors.text.secondary,
    backgroundColor: "rgba(255, 255, 255, 0.04)",
    "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
};

const notificationSkeletonBg = { bgcolor: alpha(colors.text.primary, 0.08) };

const PAPER_MOTION = {
    initial: { opacity: 0, y: -8 },
    animate: { opacity: 1, y: 0 },
    transition: {
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1],
    },
};

function getLoadingSkeletonCount(notificationsLength, placeholderCount) {
    if (notificationsLength > 0) {
        return Math.min(notificationsLength, MAX_SKELETONS);
    }
    if (placeholderCount > 0) {
        return Math.min(placeholderCount, MAX_SKELETONS);
    }
    return 3;
}

function NotificationItemSkeleton() {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.7rem",
                px: "0.65rem",
                py: "0.7rem",
            }}
        >
            <Skeleton
                variant="circular"
                width={20}
                height={20}
                sx={{
                    ...notificationSkeletonBg,
                    flexShrink: 0,
                    mt: "0.15rem",
                }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton
                    variant="text"
                    width="92%"
                    height={18}
                    sx={notificationSkeletonBg}
                />
                <Skeleton
                    variant="text"
                    width="38%"
                    height={14}
                    sx={{ ...notificationSkeletonBg, mt: "0.35rem" }}
                />
            </Box>
            <Skeleton
                variant="circular"
                width={16}
                height={16}
                sx={{ ...notificationSkeletonBg, flexShrink: 0 }}
            />
        </Box>
    );
}

function groupNotificationsByDate(items) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups = {
        Today: [],
        Yesterday: [],
        Earlier: [],
    };

    items.forEach((notification) => {
        const notifDate = new Date(notification.createdAt);
        const notifDay = new Date(
            notifDate.getFullYear(),
            notifDate.getMonth(),
            notifDate.getDate(),
        );

        if (notifDay.getTime() === today.getTime()) {
            groups.Today.push(notification);
        } else if (notifDay.getTime() === yesterday.getTime()) {
            groups.Yesterday.push(notification);
        } else {
            groups.Earlier.push(notification);
        }
    });

    return groups;
}

export default function NotificationPanel({
    open,
    anchorEl,
    onClose,
    placeholderCount = 0,
}) {
    const history = useHistory();
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    const [notifications, setNotifications] = useState([]);
    const [error, setError] = useState(null);
    const hasFetchedRef = useRef(false);
    const inFlightRef = useRef(false);
    const lastFetchedAtRef = useRef(0);

    const fetchNotifications = useCallback(async () => {
        if (inFlightRef.current) {
            return;
        }

        inFlightRef.current = true;
        setError(null);

        try {
            const response = await fetch(`${basePath}/api/v1/notifications/me`, {
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error("Failed to fetch notifications");
            }

            const data = await response.json();
            setNotifications(data.notifications || []);
            hasFetchedRef.current = true;
            lastFetchedAtRef.current = Date.now();
        } catch (err) {
            console.error("Error fetching notifications:", err);
            setError(err.message);
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        const recentlyFetched =
            hasFetchedRef.current &&
            Date.now() - lastFetchedAtRef.current < REFETCH_COOLDOWN_MS;
        if (recentlyFetched) {
            return;
        }

        fetchNotifications();
    }, [open, fetchNotifications]);

    const handleMarkRead = async (notificationId) => {
        try {
            await fetch(`${basePath}/api/v1/notifications/${notificationId}/read`, {
                method: "POST",
                credentials: "include",
            });

            setNotifications((prev) =>
                prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n)),
            );
        } catch (err) {
            console.error("Error marking notification as read:", err);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await fetch(`${basePath}/api/v1/notifications/read-all`, {
                method: "POST",
                credentials: "include",
            });

            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        } catch (err) {
            console.error("Error marking all as read:", err);
        }
    };

    const handleDelete = async (notificationId) => {
        try {
            await fetch(`${basePath}/api/v1/notifications/${notificationId}`, {
                method: "DELETE",
                credentials: "include",
            });

            setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
        } catch (err) {
            console.error("Error deleting notification:", err);
        }
    };

    const handleNavigate = (url) => {
        onClose();
        history.push(url);
    };

    const handleMenuKeyDown = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            onClose(event);
        }
    };

    const groupedNotifications = groupNotificationsByDate(notifications);
    const unreadCount = notifications.filter((n) => !n.read).length;
    const showSkeletons = open && !hasFetchedRef.current && !error;
    const loadingSkeletonCount = getLoadingSkeletonCount(
        notifications.length,
        placeholderCount,
    );

    const paperSx = {
        position: "relative",
        overflow: "hidden",
        width: isMobile ? "100%" : { xs: "22rem", xl: "24rem" },
        height: isMobile ? "100%" : "auto",
        maxHeight: isMobile ? "none" : "36rem",
        display: "flex",
        flexDirection: "column",
        borderRadius: "8px",
        background: `
            linear-gradient(
                180deg,
                #101012 0%,
                #08080A 100%
            )
        `,
        border: "1px solid rgba(255,255,255,0.06)",
        boxShadow: `
            0 30px 80px rgba(0,0,0,0.58),
            inset 0 1px 0 rgba(255,255,255,0.05),
            0 0 0 1px rgba(255,255,255,0.02)
        `,
        p: "0.75rem 1rem",
        ...(isMobile
            ? {}
            : {
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background: `
                        radial-gradient(
                            circle at top right,
                            rgba(232,163,61,0.12),
                            transparent 38%
                        )
                    `,
                },
            }),
    };

    const menuBody = (
        <Box
            sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                maxHeight: isMobile ? "none" : "calc(100vh - 6rem)",
            }}
            onKeyDown={handleMenuKeyDown}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.65rem",
                    pb: isMobile ? "1rem" : "0.7rem",
                    pt: isMobile ? "0.35rem" : 0,
                    mb: isMobile ? "0.45rem" : "0.15rem",
                    borderBottom: "1px solid rgba(255,255,255,0.065)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "0.45rem",
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            letterSpacing: "0.03em",
                            color: colors.text.primary,
                        }}
                    >
                        Notifications
                    </Typography>
                    {unreadCount > 0 ? (
                        <Typography
                            sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: colors.primary.main,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {unreadCount}
                        </Typography>
                    ) : null}
                </Box>

                {!isMobile && unreadCount > 0 ? (
                    <Box
                        component="button"
                        type="button"
                        onClick={handleMarkAllRead}
                        sx={MARK_ALL_READ_SX}
                    >
                        <CheckOutlined sx={{ fontSize: "0.95rem" }} />
                        Mark all read
                    </Box>
                ) : null}

                {isMobile ? (
                    <IconButton
                        onClick={onClose}
                        aria-label="Close notifications"
                        sx={{
                            ...CLOSE_BUTTON_SX,
                            flexShrink: 0,
                        }}
                    >
                        <Close sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                ) : null}
            </Box>

            {isMobile && unreadCount > 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pt: "0.7rem",
                        pb: "0.15rem",
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        onClick={handleMarkAllRead}
                        sx={MARK_ALL_READ_SX}
                    >
                        <CheckOutlined sx={{ fontSize: "0.95rem" }} />
                        Mark all read
                    </Box>
                </Box>
            ) : null}

            <Box
                data-lenis-prevent
                sx={{
                    flex: 1,
                    minHeight: 0,
                    maxHeight: isMobile ? "none" : "28rem",
                    overflowY: "auto",
                    overflowX: "hidden",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": {
                        display: "none",
                        width: 0,
                        height: 0,
                    },
                }}
            >
                {showSkeletons ? (
                    <Box sx={{ py: "0.2rem" }}>
                        {Array.from({ length: loadingSkeletonCount }).map((_, index) => (
                            <NotificationItemSkeleton
                                key={`notification-skeleton-${index}`}
                            />
                        ))}
                    </Box>
                ) : error && notifications.length === 0 ? (
                    <Box sx={{ py: "2.25rem", px: "0.5rem", textAlign: "center" }}>
                        <Typography sx={{ color: colors.state.danger.main, fontSize: "0.85rem" }}>
                            Failed to load notifications
                        </Typography>
                        <Button
                            onClick={() => fetchNotifications()}
                            sx={{
                                mt: "0.75rem",
                                color: colors.primary.main,
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            Retry
                        </Button>
                    </Box>
                ) : notifications.length === 0 ? (
                    <Box
                        sx={{
                            py: "2.4rem",
                            px: "0.5rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "0.65rem",
                            textAlign: "center",
                        }}
                    >
                        <NotificationsNoneOutlined
                            sx={{
                                fontSize: "1.55rem",
                                color: colors.text.muted,
                            }}
                        />
                        <Typography sx={{ color: colors.text.secondary, fontSize: "0.82rem" }}>
                            No notifications yet
                        </Typography>
                    </Box>
                ) : (
                    Object.entries(groupedNotifications).map(
                        ([groupName, groupNotifications]) =>
                            groupNotifications.length > 0 ? (
                                <Box key={groupName}>
                                    <Typography
                                        sx={{
                                            px: "0.65rem",
                                            pt: "0.8rem",
                                            pb: "0.3rem",
                                            fontSize: "0.62rem",
                                            fontWeight: 800,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            color: colors.text.secondary,
                                        }}
                                    >
                                        {groupName}
                                    </Typography>
                                    <AnimatePresence initial={false}>
                                        {groupNotifications.map((notification) => (
                                            <NotificationItem
                                                key={notification._id}
                                                notification={notification}
                                                onMarkRead={handleMarkRead}
                                                onDelete={handleDelete}
                                                onNavigate={handleNavigate}
                                            />
                                        ))}
                                    </AnimatePresence>
                                </Box>
                            ) : null,
                    )
                )}
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <BackdropOverlay
                open={open}
                onClick={onClose}
                zIndex={10000}
                sx={{ alignItems: "stretch" }}
                contentSx={{
                    width: "100%",
                    maxWidth: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <MotionPaper
                    initial={PAPER_MOTION.initial}
                    animate={PAPER_MOTION.animate}
                    transition={PAPER_MOTION.transition}
                    sx={paperSx}
                >
                    {menuBody}
                </MotionPaper>
            </BackdropOverlay>
        );
    }

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            role={undefined}
            placement="bottom-end"
            sx={{ zIndex: 10000 }}
            disablePortal
            modifiers={[
                {
                    name: "offset",
                    options: {
                        offset: [0, 4],
                    },
                },
            ]}
        >
            <MotionPaper
                initial={PAPER_MOTION.initial}
                animate={PAPER_MOTION.animate}
                transition={PAPER_MOTION.transition}
                sx={{
                    ...paperSx,
                    transformOrigin: "top right",
                }}
            >
                <ClickAwayListener onClickAway={onClose}>
                    {menuBody}
                </ClickAwayListener>
            </MotionPaper>
        </Popper>
    );
}

NotificationPanel.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    placeholderCount: PropTypes.number,
};
